import { AppFunctionComponent } from "../../../types"
import { Block, BLOCKS, Node, Text } from "@contentful/rich-text-types"
import { graphql, useStaticQuery } from "gatsby"
import React from "react"
import styled from "styled-components"
import { useLocalizationContext } from "../../../context/localization.context"
import RichText from "../../../resolvers/rich-text/rich-text.resolver"
import {
  ContentfulSectionElement,
  SectionQueryResultTranslated,
} from "../../../types"
import { SecondaryHeading } from "../../common/heading.component"
import Section, { SectionImage } from "../../common/sections/section.component"
import { Navigation, Slider, Slides } from "../../common/slider/slider"
import Timeline from "./timeline.component"

const XRaySection = styled(Section)`
  @media (max-width: ${({ theme }) => theme.breakpoint.desktop - 1}px) {
    width: 100%;
  }
`

const XRaySlider = styled(Slider)`
  display: grid;
  grid-template-rows: auto 90px auto 250px;

  @media (min-width: ${({ theme }) => theme.breakpoint.desktop}px) {
    grid-column-gap: 120px;
    grid-template-columns: 1fr 380px;
    grid-template-rows: 220px auto 80px;
    padding-right: 50px;
  }
`

const TimelineSlides = styled(Slides)`
  grid-area: 4 / 1 / 4 / 1;

  @media (min-width: ${({ theme }) => theme.breakpoint.desktop}px) {
    grid-area: 1 / 1 / 4 / 1;
  }
`

const TextSlides = styled(Slides)`
  grid-area: 3 / 1 / 3 / 1;
  text-align: center;
  padding-bottom: 60px;
  margin: 0 20px;

  @media (min-width: ${({ theme }) => theme.breakpoint.desktop}px) {
    grid-area: 1 / 2 / 1 / 2;
    padding-bottom: 0;
    margin: 0;
  }

  ${SecondaryHeading} {
    margin-top: 0;
  }
`

const ImageSlides = styled(Slides)`
  grid-area: 1 / 1 / 1 / 1;
  max-width: 420px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 0 20px;

  @media (min-width: ${({ theme }) => theme.breakpoint.desktop}px) {
    grid-area: 2 / 2 / 2 / 2;
    max-width: 380px;
    padding: 0;
  }
`

const SlidesNavigation = styled(Navigation)`
  grid-area: 2 / 1 / 2 / 1;
  justify-content: center;
  align-items: center;
  margin: 0 20px;

  @media (min-width: ${({ theme }) => theme.breakpoint.desktop}px) {
    grid-area: 3 / 2 / 3 / 2;
    margin: 0;
  }
`

const mapToEvents = (sectionElement: ContentfulSectionElement[]) => {
  return sectionElement.map(({ text: t }) => {
    return JSON.parse(t!.raw)
      .content.filter(({ nodeType }: Node) => nodeType === "unordered-list")
      .map(({ content }: any) => {
        const [time, text] = (content as Block[]).map((item) => {
          return ((item.content[0] as Block).content[0] as Text).value
        })
        return { time, text }
      })
  })
}

const XRay: AppFunctionComponent = () => {
  const {
    content: { nodes },
  }: SectionQueryResultTranslated = useStaticQuery(query)
  const { locale } = useLocalizationContext()
  const translatedContent =
    nodes.find((node) => node.node_locale === locale) || nodes[0]
  const { sectionElement } = translatedContent

  const eventsMap = mapToEvents(sectionElement!)
  const title = "X-Ray"
  return (
    <XRaySection>
      <XRaySlider title={title}>
        <TimelineSlides>
          {eventsMap.map((events, index) => (
            <Timeline key={"timeline-" + index} events={events} />
          ))}
        </TimelineSlides>
        <TextSlides>
          {sectionElement &&
            sectionElement.map((element) => {
              return (
                <>
                  {element.text && (
                    <RichText
                      key={element.id}
                      content={element.text}
                      options={{ [BLOCKS.UL_LIST]: () => <></> }}
                    />
                  )}
                </>
              )
            })}
        </TextSlides>
        <ImageSlides>
          {sectionElement &&
            sectionElement.map((element) => {
              return (
                <SectionImage
                  key={element.id}
                  image={element.image!.gatsbyImageData}
                />
              )
            })}
        </ImageSlides>
        <SlidesNavigation title={title} />
      </XRaySlider>
    </XRaySection>
  )
}

export default XRay

const query = graphql`
  query SarCheckerXRaySectionQuery {
    content: allContentfulSection(
      filter: { contentfulid: { eq: "sar-checker/x-ray" } }
    ) {
      nodes {
        node_locale
        sectionElement {
          text {
            raw
            references {
              ...Footnote
            }
          }
          image {
            description
            gatsbyImageData(
              layout: CONSTRAINED
              placeholder: BLURRED
              quality: 100
              width: 380
            )
          }
        }
      }
    }
  }
`
